/**
 * Helpers for deriving an intake background color from a law firm's logo and
 * for computing legible foreground colors on top of an arbitrary background.
 */

export type RGB = { r: number; g: number; b: number };

const clampByte = (n: number): number =>
  Math.max(0, Math.min(255, Math.round(n)));

const toHex = (n: number): string => clampByte(n).toString(16).padStart(2, "0");

export function rgbToHex({ r, g, b }: RGB): string {
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Parses `#RGB` / `#RRGGBB` (or the same without `#`) into an RGB triple. */
export function hexToRgb(hex: string): RGB | null {
  const raw = String(hex || "")
    .trim()
    .replace(/^#/, "");
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b };
  }
  if (raw.length === 6) {
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b };
  }
  return null;
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function getRelativeLuminance({ r, g, b }: RGB): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** True when white text is more legible than dark text on the given color. */
export function isDarkColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;
  return getRelativeLuminance(rgb) < 0.5;
}

/**
 * Loads a logo image and estimates its background color by sampling the pixels
 * along its edges (where a logo's canvas/background typically shows). Resolves
 * to a `#RRGGBB` string, or `null` when the image can't be loaded or the canvas
 * is CORS-tainted (in which case callers should fall back to a manual color).
 */
export function sampleImageBackgroundColor(
  url: string
): Promise<string | null> {
  return new Promise((resolve) => {
    const src = String(url || "").trim();
    if (!src || typeof document === "undefined") {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";

    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    // Guard against images that never fire load/error.
    const timeout = window.setTimeout(() => finish(null), 8000);

    img.onerror = () => {
      window.clearTimeout(timeout);
      finish(null);
    };

    img.onload = () => {
      window.clearTimeout(timeout);
      try {
        const naturalW = img.naturalWidth || img.width;
        const naturalH = img.naturalHeight || img.height;
        if (!naturalW || !naturalH) {
          finish(null);
          return;
        }

        // Downscale for cheap sampling while keeping aspect ratio.
        const maxDim = 64;
        const scale = Math.min(1, maxDim / Math.max(naturalW, naturalH));
        const w = Math.max(1, Math.round(naturalW * scale));
        const h = Math.max(1, Math.round(naturalH * scale));

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);

        // Reading pixels throws a SecurityError if the canvas is tainted.
        const { data } = ctx.getImageData(0, 0, w, h);

        // Aggregate sampled pixels, weighting by frequency so the dominant
        // background color wins. Logos sometimes have transparent padding, so
        // we first try the edges and then fall back to all opaque pixels.
        const buckets = new Map<
          string,
          { count: number; r: number; g: number; b: number }
        >();
        const consider = (x: number, y: number) => {
          const idx = (y * w + x) * 4;
          const a = data[idx + 3];
          if (a < 200) return; // ignore transparent padding
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          // Quantize to reduce noise from anti-aliasing / gradients.
          const key = `${Math.floor(r / 16)}-${Math.floor(g / 16)}-${Math.floor(
            b / 16
          )}`;
          const bucket = buckets.get(key);
          if (bucket) {
            bucket.count += 1;
            bucket.r += r;
            bucket.g += g;
            bucket.b += b;
          } else {
            buckets.set(key, { count: 1, r, g, b });
          }
        };

        const chooseBestBucket = () => {
          let best: { count: number; r: number; g: number; b: number } | null =
            null;
          for (const bucket of buckets.values()) {
            if (!best || bucket.count > best.count) best = bucket;
          }
          return best;
        };

        for (let x = 0; x < w; x += 1) {
          consider(x, 0);
          consider(x, h - 1);
        }
        for (let y = 0; y < h; y += 1) {
          consider(0, y);
          consider(w - 1, y);
        }

        let best = chooseBestBucket();

        if (!best) {
          for (let y = 0; y < h; y += 1) {
            for (let x = 0; x < w; x += 1) {
              consider(x, y);
            }
          }
          best = chooseBestBucket();
        }

        if (!best) {
          finish(null);
          return;
        }

        finish(
          rgbToHex({
            r: best.r / best.count,
            g: best.g / best.count,
            b: best.b / best.count
          })
        );
      } catch {
        // Tainted canvas (CORS) or any unexpected error -> fall back.
        finish(null);
      }
    };

    img.src = src;
  });
}
